import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import ModalStatus from 'materials/ModalStatus';
import ModalConfirmation from 'components/ModalConfirmation';
import ModalAddSubjectComponent from '../Component/ModalAddSubjectComponent';
import {
  deleteComplaintSubject,
  postComplaintSubject,
  putComplaintSubject,
} from 'services/ComplainSubjectService';
import { SelectOptionsSubjectInterface } from 'interface/general';

type ModalAddSubjectProps = {
  onCancel: () => void;
  refetchSubjectList: () => void;
  isShow: boolean;
  dataSubjectList: SelectOptionsSubjectInterface[];
};

export default function ModalAddSubjectContainer({
  isShow,
  onCancel,
  refetchSubjectList,
  dataSubjectList,
}: ModalAddSubjectProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [subjectValue, setSubjectValue] = useState('');
  const [subjectId, setSubjectId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const clearForm = () => {
    setSubjectValue('');
    setSubjectId(null);
  };

  const handleClickSave = () => {
    setShowConfirm(prev => !prev);
  };

  const handleSubmitData = () => {
    const payload = { subjectComplaint: subjectValue };
    if (subjectId !== null) {
      updateSubject({ id: subjectId, data: payload });
    } else {
      submitSubject(payload);
    }
  };

  const { mutate: submitSubject, isLoading: isLoadingSubmit } = useMutation(
    postComplaintSubject,
    {
      onSuccess: res => {
        ModalStatus({
          status: 'success',
          title: 'Berhasil Ditambahkan',
          content: 'Subjek berhasil ditambahkan.',
        });
        handleClickSave();
        onCancel();
        clearForm();
        refetchSubjectList();
      },
      onError: () => {
        ModalStatus({
          status: 'error',
          title: 'Gagal Menambahkan',
          content: 'Silakan hubungi admin.',
        });
        clearForm();
      },
    }
  );

  const { mutate: updateSubject, isLoading: isLoadingUpdate } = useMutation(
    ({ id, data }: { id: number; data: { subjectComplaint: string } }) =>
      putComplaintSubject(id, data),
    {
      onSuccess: res => {
        ModalStatus({
          status: 'success',
          title: 'Berhasil Diubah',
          content: 'Subjek berhasil diperbarui.',
        });
        handleClickSave();
        onCancel();
        clearForm();
        refetchSubjectList();
      },
      onError: () => {
        ModalStatus({
          status: 'error',
          title: 'Gagal Mengubah',
          content: 'Silakan hubungi admin.',
        });
        clearForm();
      },
    }
  );

  const handleDelete = (id: number) => {
    setConfirmDeleteId(id);
  };

  const { mutate: deleteSubject, isLoading: isLoadingDelete } = useMutation(
    deleteComplaintSubject,
    {
      onSuccess: () => {
        ModalStatus({
          status: 'success',
          title: 'Berhasil Dihapus',
          content: 'Subjek berhasil dihapus.',
        });
        setConfirmDeleteId(null);
        refetchSubjectList();
      },
      onError: () => {
        ModalStatus({
          status: 'error',
          title: 'Gagal Menghapus',
          content: 'Silakan hubungi admin.',
        });
        setConfirmDeleteId(null);
      },
    }
  );

  const handleConfirmDelete = () => {
    if (confirmDeleteId !== null) {
      deleteSubject(confirmDeleteId);
    }
  };

  return (
    <>
      <ModalConfirmation
        visibleModal={showConfirm}
        title="Apakah anda yakin?"
        description="Pastikan data subjek sudah sesuai."
        onConfirm={handleClickSave}
        onCancel={handleSubmitData}
        isLoadingYa={isLoadingSubmit || isLoadingUpdate}
        okeText="TIDAK"
        cancelText="YA"
      />
      <ModalConfirmation
        visibleModal={confirmDeleteId !== null}
        title="Konfirmasi Hapus Subjek"
        description="Apakah Anda yakin ingin menghapus subjek ini?"
        onConfirm={() => setConfirmDeleteId(null)} // tombol "Tidak"
        onCancel={handleConfirmDelete} // tombol "Ya"
        isLoadingYa={isLoadingDelete}
        okeText="TIDAK"
        cancelText="YA"
      />
      <ModalAddSubjectComponent
        isShow={isShow}
        onCancel={onCancel}
        onSave={handleClickSave}
        dataSubjectList={dataSubjectList}
        subjectValue={subjectValue}
        setSubjectValue={setSubjectValue}
        setSubjectId={setSubjectId}
        handleDelete={handleDelete}
      />
    </>
  );
}
